import { lazyComponent } from "@redotech/react-util/component";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { PopupEvent } from "@redotech/redo-onboarding-common/popup-events";
import { FeatureOnboardingType } from "@redotech/redo-onboarding-common/user-onboarding";
import { assertNever } from "@redotech/util/type";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isWmsRoute } from "../app/wms-route";
import { isWelcomeRoute } from "../onboarding/first-install-redirect";
import * as styles from "./index.module.css";
import { useOnboardingPreviewType } from "./onboarding-preview";
import { isPopupVisibleToMerchant } from "./popup-analytics";
import { useFeatureOnboardingManager } from "./use-feature-onboarding";
import { useWithdrawalsOnboardingRequestId } from "./withdrawals-onboarding-trigger";

const AutoClaimsUpsellModal = lazyComponent(() =>
  import("@redotech/redo-merchant-fulfillment-app/common/shipping-claim/auto-claims-upsell-modal").then(
    (module) => module.AutoClaimsUpsellModal,
  ),
);

const ManagedReturnsTrialModal = lazyComponent(() =>
  import("./managed-returns-trial-onboarding/managed-returns-trial-modal").then(
    (module) => module.ManagedReturnsTrialModal,
  ),
);

const AgenticCatalogOnboarding = lazyComponent(() =>
  import("./onboarding-cards/agentic-catalog-onboarding").then(
    (module) => module.AgenticCatalogOnboarding,
  ),
);

const BillingSettingsConsolidatedOnboarding = lazyComponent(() =>
  import("./onboarding-cards/billing-settings-consolidated").then(
    (module) => module.BillingSettingsConsolidatedOnboarding,
  ),
);

const BulkReturnActionsOnboarding = lazyComponent(() =>
  import("./onboarding-cards/bulk-return-actions").then(
    (module) => module.BulkReturnActionsOnboarding,
  ),
);

const ConversionOnboarding = lazyComponent(() =>
  import("./onboarding-cards/conversion-onboarding").then(
    (module) => module.ConversionOnboarding,
  ),
);

const ExpandedWarrantyOnboarding = lazyComponent(() =>
  import("./onboarding-cards/expanded-warranty-onboarding").then(
    (module) => module.ExpandedWarrantyOnboarding,
  ),
);

const FeedbackAnnouncementOnboarding = lazyComponent(() =>
  import("./onboarding-cards/feedback-announcement").then(
    (module) => module.FeedbackAnnouncementOnboarding,
  ),
);

const HelpIconMovingOnboarding = lazyComponent(() =>
  import("./onboarding-cards/help-icon-moving").then(
    (module) => module.HelpIconMovingOnboarding,
  ),
);

const IntercomMovedOnboarding = lazyComponent(() =>
  import("./onboarding-cards/intercom-moved").then(
    (module) => module.IntercomMovedOnboarding,
  ),
);

const ManagedReturnsOnboarding = lazyComponent(() =>
  import("./onboarding-cards/managed-returns").then(
    (module) => module.ManagedReturnsOnboarding,
  ),
);

const NewHomeOnboarding = lazyComponent(() =>
  import("./onboarding-cards/new-home").then(
    (module) => module.NewHomeOnboarding,
  ),
);

const PackageProtectionPlusOnboarding = lazyComponent(() =>
  import("./onboarding-cards/package-protection-plus").then(
    (module) => module.PackageProtectionPlusOnboarding,
  ),
);

const ReclaimAnnouncementOnboarding = lazyComponent(() =>
  import("./onboarding-cards/reclaim-announcement").then(
    (module) => module.ReclaimAnnouncementOnboarding,
  ),
);

const AiSupportActionNeededV2Modal = lazyComponent(() =>
  import("./onboarding-modals/ai-support-action-needed-v2-modal").then(
    (module) => module.AiSupportActionNeededV2Modal,
  ),
);

const CustomsReadinessModal = lazyComponent(() =>
  import("./onboarding-modals/customs-readiness-modal").then(
    (module) => module.CustomsReadinessModal,
  ),
);

const DhlFreeLabelsAnnouncementModal = lazyComponent(() =>
  import("./onboarding-modals/dhl-free-labels-announcement-modal").then(
    (module) => module.DhlFreeLabelsAnnouncementModal,
  ),
);

const GivingDayTeaserModal = lazyComponent(() =>
  import("./onboarding-modals/giving-day-teaser-modal").then(
    (module) => module.GivingDayTeaserModal,
  ),
);

const HomepageAnalyticsMovedModal = lazyComponent(() =>
  import("./onboarding-modals/homepage-analytics-moved-modal").then(
    (module) => module.HomepageAnalyticsMovedModal,
  ),
);

const McpAnnouncementModal = lazyComponent(() =>
  import("./onboarding-modals/mcp-announcement-modal").then(
    (module) => module.McpAnnouncementModal,
  ),
);

const OutboundLabelsUpsellOnboarding = lazyComponent(() =>
  import("./onboarding-modals/outbound-labels-upsell").then(
    (module) => module.OutboundLabelsUpsellOnboarding,
  ),
);

const CommerceAgentLaunchModal = lazyComponent(() =>
  import("./onboarding-modals/recover-product-announcement-modal").then(
    (module) => module.CommerceAgentLaunchModal,
  ),
);

const RecoverNonSubscriberAnnouncementModal = lazyComponent(() =>
  import("./onboarding-modals/recover-product-announcement-modal").then(
    (module) => module.RecoverNonSubscriberAnnouncementModal,
  ),
);

const RecoverNonSubscriberOffer2500Modal = lazyComponent(() =>
  import("./onboarding-modals/recover-product-announcement-modal").then(
    (module) => module.RecoverNonSubscriberOffer2500Modal,
  ),
);

const RecoverNonSubscriberOffer5000Modal = lazyComponent(() =>
  import("./onboarding-modals/recover-product-announcement-modal").then(
    (module) => module.RecoverNonSubscriberOffer5000Modal,
  ),
);

const RecoverSubscriberAnnouncementModal = lazyComponent(() =>
  import("./onboarding-modals/recover-product-announcement-modal").then(
    (module) => module.RecoverSubscriberAnnouncementModal,
  ),
);

const Redo3plInterestSurveyOnboarding = lazyComponent(() =>
  import("./onboarding-modals/redo-3pl-interest-survey-modal").then(
    (module) => module.Redo3plInterestSurveyOnboarding,
  ),
);

const SuppliesFreeLabelsAnnouncementModal = lazyComponent(() =>
  import("./onboarding-modals/supplies-free-labels-announcement-modal").then(
    (module) => module.SuppliesFreeLabelsAnnouncementModal,
  ),
);

const SuppliesProductAnnouncementModal = lazyComponent(() =>
  import("./onboarding-modals/supplies-product-announcement-modal").then(
    (module) => module.SuppliesProductAnnouncementModal,
  ),
);

const WithdrawalsOnboardingModal = lazyComponent(() =>
  import("./withdrawals-onboarding/withdrawals-onboarding-modal").then(
    (module) => module.WithdrawalsOnboardingModal,
  ),
);

export const FeatureOnboarding = memo(function FeatureOnboarding() {
  const screenType = useScreenType();
  const {
    onboardingType: managerType,
    showOnboarding: managerShow,
    handleDismiss,
    handleComplete,
    dismissOnboardingType,
    completeOnboardingType,
    trackOnboardingEvent,
  } = useFeatureOnboardingManager();

  const location = useLocation();
  const navigate = useNavigate();
  const managedReturnsTrialPreview = useManagedReturnsTrialPreview();
  const withdrawalsOnboardingRequestId = useWithdrawalsOnboardingRequestId();
  const [
    dismissedWithdrawalsOnboardingRequestId,
    setDismissedWithdrawalsOnboardingRequestId,
  ] = useState(0);
  const previewType = useOnboardingPreviewType();

  const manualWithdrawalsOnboardingOpen =
    withdrawalsOnboardingRequestId > dismissedWithdrawalsOnboardingRequestId;
  const onboardingType = manualWithdrawalsOnboardingOpen
    ? FeatureOnboardingType.WITHDRAWALS_ONBOARDING
    : (previewType ?? managerType);
  const showOnboarding = manualWithdrawalsOnboardingOpen ? true : managerShow;

  const card = useMemo(() => {
    const isWithdrawalsOnboarding =
      onboardingType === FeatureOnboardingType.WITHDRAWALS_ONBOARDING;
    if (screenType === ScreenType.MOBILE && !isWithdrawalsOnboarding) {
      return null;
    }
    // Hide from Playwright unless `?onboarding-preview` is set.
    if (
      !isWithdrawalsOnboarding &&
      !previewType &&
      typeof navigator !== "undefined" &&
      navigator.webdriver
    ) {
      return null;
    }
    if (
      !onboardingType ||
      !showOnboarding ||
      onboardingType === FeatureOnboardingType.DISABLED
    ) {
      return null;
    }
    switch (onboardingType) {
      case FeatureOnboardingType.PACKAGE_PROTECTION_PLUS:
        return (
          <PackageProtectionPlusOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.EXPANDED_WARRANTY:
        return (
          <ExpandedWarrantyOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.MANAGED_RETURNS:
        return (
          <ManagedReturnsOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.CONVERSION:
        return (
          <ConversionOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.INTERCOM_MOVED:
        return (
          <IntercomMovedOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.SUPPORT_TO_AI_UPSELL:
        return (
          <AiSupportActionNeededV2Modal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.BILLING_SETTINGS_CONSOLIDATED:
        return (
          <BillingSettingsConsolidatedOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.HELP_ICON_MOVING:
        return (
          <HelpIconMovingOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.AGENTIC_CATALOG:
        return (
          <AgenticCatalogOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_UPS:
      case FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_AMAZON:
      case FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_DHL:
        return (
          <OutboundLabelsUpsellOnboarding
            carrier={onboardingType}
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.HOMEPAGE_ANALYTICS_MOVED:
        return (
          <HomepageAnalyticsMovedModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.RECLAIM_ANNOUNCEMENT:
        return (
          <ReclaimAnnouncementOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.BULK_RETURN_ACTIONS:
        return (
          <BulkReturnActionsOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.SHIPPING_CLAIMS_AUTO_FILING:
        return (
          <AutoClaimsUpsellModal
            onAccept={handleComplete}
            onClose={handleDismiss}
            open
          />
        );
      case FeatureOnboardingType.SUPPLIES_PRODUCT_ANNOUNCEMENT:
        return (
          <SuppliesProductAnnouncementModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.RECOVER_SUBSCRIBER_ANNOUNCEMENT:
        return (
          <RecoverSubscriberAnnouncementModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.RECOVER_NON_SUBSCRIBER_ANNOUNCEMENT:
        return (
          <RecoverNonSubscriberAnnouncementModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.RECOVER_NON_SUBSCRIBER_OFFER_2500:
        return (
          <RecoverNonSubscriberOffer2500Modal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.RECOVER_NON_SUBSCRIBER_OFFER_5000:
        return (
          <RecoverNonSubscriberOffer5000Modal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.SUPPLIES_FREE_LABELS_ANNOUNCEMENT:
        return (
          <SuppliesFreeLabelsAnnouncementModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.DHL_FREE_LABELS_ANNOUNCEMENT:
        return (
          <DhlFreeLabelsAnnouncementModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.FEEDBACK_ANNOUNCEMENT:
        return (
          <FeedbackAnnouncementOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.REDO_3PL_INTEREST_SURVEY:
        return (
          <Redo3plInterestSurveyOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.MANAGED_RETURNS_TRIAL:
        return (
          <ManagedReturnsTrialModal
            onClose={handleDismiss}
            onComplete={handleComplete}
          />
        );
      case FeatureOnboardingType.WITHDRAWALS_ONBOARDING:
        return (
          <WithdrawalsOnboardingModal
            onClose={() => {
              if (manualWithdrawalsOnboardingOpen) {
                setDismissedWithdrawalsOnboardingRequestId(
                  withdrawalsOnboardingRequestId,
                );
                void dismissOnboardingType(
                  FeatureOnboardingType.WITHDRAWALS_ONBOARDING,
                );
                return;
              }
              void handleDismiss();
            }}
            onComplete={() => {
              if (manualWithdrawalsOnboardingOpen) {
                setDismissedWithdrawalsOnboardingRequestId(
                  withdrawalsOnboardingRequestId,
                );
                void completeOnboardingType(
                  FeatureOnboardingType.WITHDRAWALS_ONBOARDING,
                );
                return;
              }
              void handleComplete();
            }}
          />
        );
      case FeatureOnboardingType.COMMERCE_AGENT_LAUNCH:
        return (
          <CommerceAgentLaunchModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.NEW_HOME:
        return (
          <NewHomeOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.CUSTOMS_READINESS_POPUP:
        return (
          <CustomsReadinessModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.MCP_ANNOUNCEMENT:
        return (
          <McpAnnouncementModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.GIVING_DAY_TEASER:
        return (
          <GivingDayTeaserModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.SIGNUP_FORMS_REPLACED_BY_POPUPS:
      case FeatureOnboardingType.CUSTOMS_INFORMATION_POPUP:
      case FeatureOnboardingType.INTERNATIONAL_RETURNS:
      case FeatureOnboardingType.CROSS_BORDER_ONBOARDING:
      case FeatureOnboardingType.OUTBOUND_VIEWS_MOVED:
      case FeatureOnboardingType.DISPUTES_WELCOME:
      case FeatureOnboardingType.AI_EXCHANGE_AGENT_UPSELL:
      case FeatureOnboardingType.ROLES_PERMISSIONS_UPDATE:
      case FeatureOnboardingType.MARKETING_TEXAS_SMS_PROTECTION:
      case FeatureOnboardingType.BLACK_FRIDAY_TIPS:
      case FeatureOnboardingType.PERMISSIONS_ROLES:
      case FeatureOnboardingType.OUTBOUND_LABELS:
      case FeatureOnboardingType.NAV_REORGANIZATION:
      case FeatureOnboardingType.SUPPORT_V2_NEW_LOOK:
        return null;
      // Cross-sell removed; kept for backward compatibility with existing DB records
      case FeatureOnboardingType.COMMENTSOLD_REDO:
      case FeatureOnboardingType.SHOPPER_AI_WRAPPED:
        return null;
      default:
        assertNever(onboardingType);
    }
  }, [
    completeOnboardingType,
    dismissOnboardingType,
    handleComplete,
    handleDismiss,
    onboardingType,
    manualWithdrawalsOnboardingOpen,
    previewType,
    showOnboarding,
    screenType,
    withdrawalsOnboardingRequestId,
  ]);

  const routeSuppressed =
    isWmsRoute(location.pathname) || isWelcomeRoute(location.pathname);

  const popupVisible = isPopupVisibleToMerchant({
    hasCard: !!card,
    routeSuppressed,
    managedReturnsTrialPreview,
    renderedType: onboardingType,
  });

  /** Cleared when the popup hides so a later re-show is counted again. */
  const shownTrackedFor = useRef<FeatureOnboardingType | undefined>(undefined);
  useEffect(() => {
    if (!popupVisible || !onboardingType) {
      shownTrackedFor.current = undefined;
      return;
    }
    if (shownTrackedFor.current === onboardingType) {
      return;
    }
    shownTrackedFor.current = onboardingType;
    trackOnboardingEvent(PopupEvent.SHOWN, onboardingType);
  }, [popupVisible, onboardingType, trackOnboardingEvent]);

  // Suppress all feature-onboarding chrome inside the welcome flow — its own
  // elevated onboarding modal owns the screen there.
  if (routeSuppressed) {
    return null;
  }

  if (managedReturnsTrialPreview) {
    return (
      <ManagedReturnsTrialModal
        onClose={() => navigate(location.pathname, { replace: true })}
      />
    );
  }

  if (!card) {
    return null;
  }

  if (
    onboardingType === FeatureOnboardingType.WITHDRAWALS_ONBOARDING ||
    onboardingType === FeatureOnboardingType.MANAGED_RETURNS_TRIAL
  ) {
    return card;
  }

  const containerClass =
    onboardingType === FeatureOnboardingType.FEEDBACK_ANNOUNCEMENT
      ? styles.containerTopRight
      : styles.container;

  return <div className={containerClass}>{card}</div>;
});

function useManagedReturnsTrialPreview(): boolean {
  const { search } = useLocation();
  return useMemo(
    () => new URLSearchParams(search).has("managed-returns-trial-preview"),
    [search],
  );
}
